import { useState } from "react";
import { User, multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Smartphone, XCircle } from "lucide-react";

interface SMSMFASetupProps {
  user: User | null;
  onComplete: () => void;
}

export function SMSMFASetup({ user, onComplete }: SMSMFASetupProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"phone" | "verify">("phone");

  const initializeRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth(), 'recaptcha-container', {
        size: 'normal',
        callback: () => {
          if (import.meta.env.DEV) {
            console.log("reCAPTCHA solved");
          }
        },
        'expired-callback': () => {
          if (import.meta.env.DEV) {
            console.log("reCAPTCHA expired");
          }
        }
      });
    }
  };

  const sendVerificationCode = async () => {
    if (!user || !phoneNumber) {
      setError("Please enter a phone number");
      return;
    }

    // Validate phone number format (basic)
    if (!phoneNumber.startsWith("+")) {
      setError("Phone number must start with country code (e.g., +1 for US)");
      return;
    }

    setLoading(true);
    setError("");

    try {
      initializeRecaptcha();

      const session = await multiFactor(user).getSession();
      const phoneAuthProvider = new PhoneAuthProvider(auth());
      
      const phoneInfoOptions = {
        phoneNumber: phoneNumber,
        session: session,
      };

      const vId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        window.recaptchaVerifier!
      );

      setVerificationId(vId);
      setStep("verify");
      if (import.meta.env.DEV) {
        console.log("Verification code sent to:", phoneNumber);
      }
    } catch (err: any) {
      if (import.meta.env.DEV) {
        console.error("Error sending verification code:", err);
      }
      setError(err.message || "Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnroll = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a 6-digit verification code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(user!).enroll(multiFactorAssertion, "SMS verification");
      
      if (import.meta.env.DEV) {
        console.log("SMS MFA enrolled successfully");
      }
      
      // Clear reCAPTCHA
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = undefined;
      }
      
      onComplete();
    } catch (err: any) {
      if (import.meta.env.DEV) {
        console.error("Error enrolling SMS MFA:", err);
      }
      setError(err.message || "Failed to verify code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep("phone");
    setVerificationCode("");
    setVerificationId("");
    setError("");
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = undefined;
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Enable SMS Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          {step === "phone" 
            ? "Add your phone number to receive verification codes" 
            : "Enter the code sent to your phone"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === "phone" ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Include country code (e.g., +1 for US, +44 for UK)
              </p>
            </div>

            <div id="recaptcha-container" className="flex justify-center"></div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <Button
              onClick={sendVerificationCode}
              disabled={loading || !phoneNumber}
              className="w-full"
            >
              {loading ? "Sending..." : "Send Verification Code"}
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Smartphone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Code sent to {phoneNumber}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
                autoFocus
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && verificationCode.length === 6) {
                    verifyAndEnroll();
                  }
                }}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={verifyAndEnroll}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1"
              >
                {loading ? "Verifying..." : "Verify & Enable"}
              </Button>
              <Button
                variant="outline"
                onClick={resetFlow}
                disabled={loading}
              >
                Back
              </Button>
            </div>
          </>
        )}

        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Once enabled, you'll need to enter a code from your phone each time you sign in to the admin panel.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
